import type { ReactNode, RefObject } from 'react';
import { CaretLeftIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { DrawerDescription, DrawerHeader, DrawerTitle } from '@voll-intelligence/ui-kit/ui/drawer';
import { BottomSheet } from './bottom_sheet.js';

/**
 * A folha de TELA CHEIA de um campo da página no palco estreito (#2653) — o
 * mesmo `BottomSheet` da conversa estreita e dos filtros, com o cabeçalho do
 * desenho: seta de voltar + a pergunta do campo, e uma linha de contexto
 * opcional sob ela (`subtitle`: a posição do trecho no multi do voo). É a casca
 * única da folha de datas dos quatro produtos (#5662): cabeçalho de 56px com o
 * voltar em círculo de 44px. Título e linha encurtam com reticências; a seta
 * nunca sai. Voltar fecha SEM cometer nada
 * (o calendário descarta o rascunho; o Quem do hotel já aplicou o que mudou).
 * Nasceu na barra do hotel e serve à do carro: é a casca do campo que troca o
 * popover pela folha quando a largura cai, qualquer que seja o miolo.
 *
 * `footer`, quando existe, fica pregado embaixo, fora do rolo do conteúdo —
 * quem rola é o filho, que assume `flex-1`. O calendário traz o próprio pé.
 */
export function PageFieldSheet({
  open,
  onClose,
  returnFocusTo,
  title,
  subtitle,
  backLabel,
  onBack,
  footer,
  initialFocus,
  children,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Ver `BottomSheet.returnFocusTo`. */
  readonly returnFocusTo: RefObject<HTMLElement | null> | (() => HTMLElement | false);
  readonly title: string;
  /** A linha de contexto sob o título — a descrição acessível do diálogo. */
  readonly subtitle?: string;
  /** O nome acessível da seta de voltar. */
  readonly backLabel: string;
  /** A seta volta um passo DENTRO do campo (a categoria volta às carteiras) em vez
   *  de fechar. Ausente ⇒ fecha. */
  readonly onBack?: () => void;
  readonly footer?: ReactNode;
  /** Ver `BottomSheet.initialFocus`. */
  readonly initialFocus?: RefObject<HTMLElement | null>;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <BottomSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      returnFocusTo={returnFocusTo}
      fullscreen
      {...(initialFocus ? { initialFocus } : {})}
    >
      <DrawerHeader className="h-14 flex-row items-center gap-2.5 px-4 py-0 text-left!">
        <Button
          type="button"
          variant="ghost"
          size="icon-xl"
          className="shrink-0 rounded-full bg-sand-3"
          aria-label={backLabel}
          onClick={onBack ?? onClose}
        >
          <CaretLeftIcon aria-hidden="true" className="size-5" />
        </Button>
        {/* `min-w-0`: o texto encurta em vez de empurrar a seta. */}
        <div className="min-w-0 flex-1">
          <DrawerTitle className="truncate">{title}</DrawerTitle>
          {subtitle && <DrawerDescription className="truncate">{subtitle}</DrawerDescription>}
        </div>
      </DrawerHeader>
      {children}
      {footer && (
        <div className="flex flex-col gap-3 border-t border-border px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
          {footer}
        </div>
      )}
    </BottomSheet>
  );
}
